<script setup lang="ts">
/**
 * PlatformAlertBar — de meldingsbalk van Pentrical zelf, helemaal
 * bovenin het venster: boven de beheerbalk én boven de header.
 *
 * Onderhoud, een storing, een aangekondigde wijziging. Beheerd vanuit
 * het platformpaneel en gelijk in élke hub van élke klant; niets hierin
 * is vanuit een klantomgeving te wijzigen.
 *
 * Drie varianten, met oplopende urgentie:
 *   information  blauw    — rustige mededeling, weg te klikken
 *   attention    oranje   — vraagt aandacht, weg te klikken
 *   emergency    rood     — er is nú iets mis, niet weg te klikken
 *
 * Wegklikken wordt per browser onthouden (zie stores/platformAlerts),
 * niet per gebruiker: de balk staat ook boven documentatie die een
 * anonieme bezoeker leest.
 */

import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Info, TriangleAlert, X } from 'lucide-vue-next'
import { usePlatformAlertStore, type PlatformAlert } from '../../stores/platformAlerts'

const store = usePlatformAlertStore()
const { t } = useI18n()

/**
 * De vormgeving per variant. De achtergrond- en icoonkleuren liggen vast
 * (ontwerp); de tekstkleur is daarop gekozen en haalt overal ruim het
 * contrastminimum van WCAG AA (4,5:1) — wit op het rood van emergency
 * haalt 4,9:1, de donkere tekst op blauw en oranje boven de 15:1.
 *
 * De icoontjes zijn decoratief (aria-hidden): de tekst draagt de
 * boodschap, en de soort melding staat er als sr-only aanhef voor. Het
 * blauwe icoon is de merkkleur en zit met 1,7:1 op zijn achtergrond
 * bewust onder de 3:1 die voor betekenisdragende iconen zou gelden.
 */
const STIJLEN: Record<PlatformAlert['variant'], {
  background: string
  color: string
  icon: string
  component: typeof Info
}> = {
  information: {
    background: '#C9F1FC',
    color: '#0B1B20',
    icon: '#21C4F1',
    component: Info,
  },
  attention: {
    background: '#FFE5D2',
    color: '#241206',
    icon: '#B24704',
    component: TriangleAlert,
  },
  emergency: {
    background: '#E01507',
    color: '#FFFFFF',
    icon: '#FFFFFF',
    component: TriangleAlert,
  },
}

function stijl(alert: PlatformAlert) {
  return STIJLEN[alert.variant] ?? STIJLEN.information
}

/**
 * Een storing onderbreekt de schermlezer (assertive), de andere twee
 * wachten netjes hun beurt af. Zonder dit onderscheid zou een rustige
 * mededeling iemand midden in een zin onderbreken.
 */
function rol(alert: PlatformAlert): 'alert' | 'status' {
  return alert.variant === 'emergency' ? 'alert' : 'status'
}

/**
 * Kleur mag niet de enige drager van betekenis zijn (WCAG 1.4.1). Het
 * icoon is decoratief; deze - alleen voorgelezen - aanhef vertelt om wat
 * voor soort melding het gaat.
 */
function aanhef(alert: PlatformAlert): string {
  return t(`platformAlerts.variants.${alert.variant}`)
}

/**
 * Alleen http(s) doorlaten. De adressen komen van een platformbeheerder
 * en niet van klanten, maar een `javascript:`-adres in een link die op
 * élke pagina van élke klant staat is geen risico dat je op vertrouwen
 * laat rusten.
 */
function veiligeLink(url: string | null): string | null {
  if (!url) return null

  try {
    const parsed = new URL(url, window.location.origin)
    return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : null
  } catch {
    return null
  }
}

/** Verwijst de link buiten deze omgeving, dan opent hij in een nieuw tabblad. */
function isExtern(url: string): boolean {
  try {
    return new URL(url, window.location.origin).origin !== window.location.origin
  } catch {
    return false
  }
}

const meldingen = computed(() => store.visible.map(alert => ({
  alert,
  stijl: stijl(alert),
  link: veiligeLink(alert.linkUrl),
})))
</script>

<template>
  <div v-if="meldingen.length" class="platform-alerts">
    <div
      v-for="{ alert, stijl: s, link } in meldingen"
      :key="alert.id"
      class="flex items-start gap-3 px-4 py-3.5 sm:gap-4 sm:px-6"
      :style="{ backgroundColor: s.background, color: s.color }"
      :role="rol(alert)"
    >
      <!-- Het icoon staat in het ontwerp op 40px. Hier 28px: de balk is
           app-chrome boven de beheerbalk en de header, en op die schaal
           zou 40px het scherm domineren zodra er één melding staat. De
           verhouding tot de tekst blijft dezelfde. -->
      <component
        :is="s.component"
        class="w-7 h-7 flex-none"
        :style="{ color: s.icon }"
        aria-hidden="true"
      />

      <p class="flex-1 min-w-0 text-[15px] leading-7">
        <span class="sr-only">{{ aanhef(alert) }}: </span>
        {{ alert.message }}
        <!-- Whitespace tussen tekst en link is bewust een spatie in de
             markup: met alleen een marge plakt de link in een
             schermlezer aan het laatste woord vast. -->
        <a
          v-if="link"
          :href="link"
          :target="isExtern(link) ? '_blank' : undefined"
          :rel="isExtern(link) ? 'noopener noreferrer' : undefined"
          class="font-semibold underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 rounded"
          :style="{ color: s.color, '--tw-ring-color': s.color }"
        >{{ alert.linkLabel || t('platformAlerts.defaultLinkLabel') }}</a>
      </p>

      <button
        v-if="alert.dismissible"
        type="button"
        class="flex-none -my-1 -mr-2 p-2 rounded transition-opacity hover:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
        :style="{ color: s.color, '--tw-ring-color': s.color }"
        :aria-label="t('platformAlerts.dismiss')"
        @click="store.dismiss(alert)"
      >
        <X class="w-6 h-6" aria-hidden="true" />
      </button>
    </div>
  </div>
</template>
